import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    num: '01',
    icon: 'ri-search-eye-line',
    title: 'Initial Consultation',
    desc: "We analyze your current operations, contract structure, RevPAR performance, and asset positioning as part of our Hotel Revenue Management Service — at no cost and no obligation. You get a clear picture of what's working and what's not.",
    tag: 'No Cost. No Obligation.',
  },
  {
    num: '02',
    icon: 'ri-draft-line',
    title: 'Custom Partnership Agreement',
    desc: 'We build a performance-based Hotel Management Service engagement tailored to your property, your goals, and your market. No boilerplate. Your contract is written around your outcomes — not our fees.',
    tag: 'Built Around Your Goals.',
  },
  {
    num: '03',
    icon: 'ri-rocket-line',
    title: 'Execution, Reporting & Growth',
    desc: 'Your dedicated partner team activates across operations, revenue, and asset strategy as a complete Hotel Management Service. You receive weekly performance dashboards, monthly reviews, and direct access to your partner — always.',
    tag: 'Weekly Dashboards. Always On.',
  },
];

export default function HowItWorks() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-white py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="text-xs font-light text-amber-600 uppercase tracking-widest mb-4" style={{ letterSpacing: '0.25em' }}>
            How It Works
          </p>
          <h2
            className="text-4xl lg:text-5xl font-extralight text-gray-900 leading-tight"
          >
            From First Call to First Win in 30 Days
          </h2>
        </div>

        {/* Steps */}
        <div ref={ref} className="relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-16 left-1/6 right-1/6 h-px bg-gray-100" style={{ left: '16.67%', right: '16.67%' }}></div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {steps.map((step, i) => (
              <div
                key={i}
                className={`flex flex-col items-center text-center transition-all duration-700 ${
                  visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 180}ms` }}
              >
                {/* Step circle */}
                <div className="relative mb-8">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-amber-600 text-white mb-0 z-10 relative">
                    <i className={`${step.icon} text-2xl`}></i>
                  </div>
                  <span
                    className="absolute -top-3 -right-3 text-4xl font-extralight text-gray-100 select-none"
                  >
                    {step.num}
                  </span>
                </div>

                {/* Tag */}
                <span className="inline-block text-xs font-light text-amber-600 uppercase tracking-widest mb-3 px-3 py-1 bg-amber-50 rounded-full" style={{ letterSpacing: '0.15em' }}>
                  {step.tag}
                </span>

                {/* Content */}
                <h3
                  className="text-xl font-light text-gray-900 mb-4 leading-snug"
                >
                  {step.title}
                </h3>
                <p className="text-sm font-light text-gray-500 leading-relaxed max-w-xs">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA nudge */}
        <div className="mt-16 text-center">
          <p className="text-sm font-light text-gray-400">
            Average time from first call to signed agreement: <strong className="text-gray-700 font-normal">under 2 weeks.</strong>
          </p>
        </div>
      </div>
    </section>
  );
}
